
    import Vue from "vue";
    import {Component, Prop, Watch} from "vue-property-decorator";
    import {
        TutorialAttemptApi
    } from "@/Api/TutorialAttemptApi";
    import {VueInject} from "@/VueIntegration/VueInjectable";
    import {Slideshow} from "@/slideshow";
    import SequenceItem from "./SequenceItem.vue";
    import VueButton from "../UI/VueButton.vue";
    import ResultModal from "./ResultModal.vue";
    import {draggable, dropTarget} from "@/VueIntegration/Draggable";
    import Modal from "../UI/Modal.vue";
    import {MenuItem} from "./SequenceItem.vue";
    import {shuffleArray} from "@/Utility";
    import {announce} from "@/UI/Announce";
    import {IAskOrderQuestion, ICompletedOrderQuestion} from "@/Core/Interactions";
    import {IAppearance, ITutorialVariables} from "@/Core/TutorialVariables";

    @Component({
        components: {ResultModal, SequenceItem, VueButton, Modal},
        directives: {
            "draggable": draggable,
            "drop-target": dropTarget
        }
    })
    export default class Sequence extends Vue
    {
        options: Option[] = [];
        completedQuestion: ICompletedOrderQuestion|null = null;
        showResultModal = false;
        showAccessibilityModal = false;
        appearance: IAppearance;
        isSubmittingAnswers = false;
        errorMessage: string|null = null;
        lockId: number|null = null;

        selectedOptions: Option[] = [];

        //Props
        @Prop({ type: String, required: true }) readonly id: string;
        @Prop({ type: String, required: false }) readonly code: string;
        @Prop({type: Boolean, required: true}) feedback: boolean;

        //Services
        @VueInject("Slideshow") readonly slideshow: Slideshow;
        @VueInject("TutorialAttemptApi") readonly api: TutorialAttemptApi;
        @VueInject("ITutorialVariables") readonly variables: ITutorialVariables;

        // computed
        get isAnswered() {
            return this.completedQuestion !== null;
        }

        get questionCode() {
            return this.code || this.id;
        }

        get canAddMoreItems() {
            return this.question.maxAnswers > this.selectedOptions.length
                && !this.isAnswered
                && !this.isSubmittingAnswers;
        }

        get question(): IAskOrderQuestion {
            return <IAskOrderQuestion> this.api.getQuestion(this.questionCode);
        }

        get menuItems(): MenuItem[] {
            return [
                {
                    text: "Remove",
                    eventCode: "remove",
                    eventData: null
                },
                {
                    text: "Move to left",
                    eventCode: "movePrevious",
                    eventData: null
                },{
                    text: "Move to right",
                    eventCode: "moveNext",
                    eventData: null
                },
            ];
        }

        created() {
            this.appearance = this.slideshow.appearance;
        }

        mounted() {
            this.enter();
        }

        @Watch("id")
        enter() {
            this.options = [];
            this.completedQuestion = null;
            this.showResultModal = false;
            this.isSubmittingAnswers = false;
            this.errorMessage = null;
            this.lockId = null;

            this.selectedOptions = [];


            for (let code in this.question.items) {
                let item = this.question.items[code];
                this.options.push({
                    itemText: item.itemText,
                    code: item.code,
                    isCorrect: null
                });
            }

            shuffleArray(this.options);

            this.completedQuestion = <ICompletedOrderQuestion> this.api.getCompletedQuestion(this.questionCode);
            if (this.completedQuestion) {
                for (let given of this.completedQuestion.givenAnswers.sort((a, b) => (a.position > b.position) ? 1 : -1)) {
                    let option = this.options.find(x => x.code == given.questionAnswerCode);
                    if (option == undefined)
                        continue;

                    option.isCorrect = given.isCorrect;
                    this.selectedOptions.push(option);
                }

                announce("Slide featuring a sequence question that has been completed.");
            }
            else {
                this.lockId = this.slideshow.lockNext();
                announce("Slide featuring a sequence question to answer. Please go to the main region of the page.");
            }

            this.slideshow.slideEntered();
        }

        selectFromItems(option: Option) {
            if (this.selectedOptions.findIndex(o => o.code === option.code) === -1) {
                this.selectedOptions.push(option);
                announce("Added " + option.itemText + " to position " + this.selectedOptions.length + ".");
            }

        }

        remove(option: Option) {
            let i = this.selectedOptions.findIndex(x => x.code === option.code);
            if (i === -1)
                return;

            this.selectedOptions.splice(i, 1);
            announce("Removed " + option.itemText + " from your selection.");
        }

        moveNext(option: Option) {
            if (this.selectedOptions.length <= 1)
                return;

            let i = this.selectedOptions.findIndex(x => x.code === option.code);
            if (i === this.selectedOptions.length - 1) {
                announce("Cannot move answer to the right, as it is already at the end.");
                return;
            }

            announce("Moved answer " + option.itemText + " to the right.");
            this.switch(i, i + 1);
        }

        movePrevious(option: Option) {
            if (this.selectedOptions.length <= 1)
                return;

            let i = this.selectedOptions.findIndex(x => x.code === option.code);
            if (i === 0) {
                announce("Cannot move answer to the left, as it is already at the beginning.");
            }

            announce("Moved answer " + option.itemText + " to the left.");
            this.switch(i, i - 1);
        }

        // Tried a more conventional switching method, but Vue was only happy if I changed the whole array
        switch(optionIndex1 :number, optionIndex2: number) {
            let newOptions = [];
            for (let i = 0; i < this.selectedOptions.length; i++) {
                if (i === optionIndex1)
                    newOptions.push(this.selectedOptions[optionIndex2]);
                else if (i === optionIndex2)
                    newOptions.push(this.selectedOptions[optionIndex1]);
                else
                    newOptions.push(this.selectedOptions[i]);
            }

            this.selectedOptions = newOptions;
        }

        dropOnOption(droppedOptionCode: string, targetOptionCode: string) {
            let droppedOptionIndex = this.selectedOptions.findIndex(o => o.code == droppedOptionCode);
            let targetOptionIndex = this.selectedOptions.findIndex(o => o.code === targetOptionCode);

            if (droppedOptionIndex > -1) {
                this.switch(droppedOptionIndex, targetOptionIndex);
            }
        }

        submitAnswers() {
            if (this.isSubmittingAnswers || this.completedQuestion)
                return;

            let toSubmit = this.selectedOptions.map(o => o.code);

            if (toSubmit.length > this.question.maxAnswers) {
                this.errorMessage = "Too many answers selected.";
                return;
            }

            if (toSubmit.length < this.question.minAnswers) {
                this.errorMessage = "Too few answers selected.";
                return;
            }

            this.isSubmittingAnswers = true;
            this.api.submitOrderQuestion(this.questionCode, toSubmit)
                .then(result => {
                    this.isSubmittingAnswers = false;
                    this.completedQuestion = result;
                    if (this.lockId != null)
                        this.slideshow.unlockNext(this.lockId);

                    for (let option of this.selectedOptions) {
                        let answer = this.completedQuestion.givenAnswers.find(a => a.questionAnswerCode === option.code);
                        option.isCorrect = answer!.isCorrect;
                    }

                    if (this.feedback) {
                        this.showResultModal = true;
                    }
                    else {
                        this.slideshow.nextSlide();
                    }
                })
                .catch(e => {
                    console.error(e);
                    this.errorMessage = "Sorry, there's been an error processing your request. " +
                        "Please try refreshing this page and attempting to answer the question again. " +
                        "If the error persists, please contact " +
                        this.variables.customer.supportEmail;
                });
        }

        finished() {
            this.slideshow.nextSlide();
        }
    }

    interface Option {
        itemText: string;
        code: string;
        isCorrect: boolean|null;
    }
